// §11.9 — Calculator: standard and programmer modes, keyboard-driven, with a history tape.
//
// An app, so it reads theme tokens and nothing else about the skin (D-021), and it reaches the
// shell only through the `HangarAPI` it was handed (§9.1).
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import type { HangarAPI } from '@hangar/shared';
import {
  BASE_DIGITS,
  CATEGORY_LABELS,
  CONVERT_CATEGORIES,
  baseReadout,
  convert,
  formatValue,
  initialConvert,
  initialState,
  parseEntry,
  setConvertCategory,
  unitsOf,
  pressBackspace,
  pressClear,
  pressDecimalPoint,
  pressDigit,
  pressEquals,
  pressNegate,
  pressOperator,
  setBase,
  setMode,
  type Base,
  type CalculatorState,
  type Mode,
  type Operator,
} from './engine';

type Action =
  | { type: 'digit'; digit: string }
  | { type: 'point' }
  | { type: 'operator'; operator: Operator }
  | { type: 'equals' }
  | { type: 'clear' }
  | { type: 'backspace' }
  | { type: 'negate' }
  | { type: 'mode'; mode: Mode }
  | { type: 'base'; base: Base };

function reduce(state: CalculatorState, action: Action): CalculatorState {
  switch (action.type) {
    case 'digit':
      return pressDigit(state, action.digit);
    case 'point':
      return pressDecimalPoint(state);
    case 'operator':
      return pressOperator(state, action.operator);
    case 'equals':
      return pressEquals(state);
    case 'clear':
      return pressClear(state);
    case 'backspace':
      return pressBackspace(state);
    case 'negate':
      return pressNegate(state);
    case 'mode':
      return setMode(state, action.mode);
    case 'base':
      return setBase(state, action.base);
  }
}

const STANDARD_KEYS: Array<{ label: string; action: Action; kind?: string }> = [
  { label: 'C', action: { type: 'clear' }, kind: 'function' },
  { label: '±', action: { type: 'negate' }, kind: 'function' },
  { label: '⌫', action: { type: 'backspace' }, kind: 'function' },
  { label: '÷', action: { type: 'operator', operator: '÷' }, kind: 'operator' },
  { label: '7', action: { type: 'digit', digit: '7' } },
  { label: '8', action: { type: 'digit', digit: '8' } },
  { label: '9', action: { type: 'digit', digit: '9' } },
  { label: '×', action: { type: 'operator', operator: '×' }, kind: 'operator' },
  { label: '4', action: { type: 'digit', digit: '4' } },
  { label: '5', action: { type: 'digit', digit: '5' } },
  { label: '6', action: { type: 'digit', digit: '6' } },
  { label: '−', action: { type: 'operator', operator: '−' }, kind: 'operator' },
  { label: '1', action: { type: 'digit', digit: '1' } },
  { label: '2', action: { type: 'digit', digit: '2' } },
  { label: '3', action: { type: 'digit', digit: '3' } },
  { label: '+', action: { type: 'operator', operator: '+' }, kind: 'operator' },
  { label: '0', action: { type: 'digit', digit: '0' } },
  { label: '.', action: { type: 'point' } },
  { label: '=', action: { type: 'equals' }, kind: 'equals' },
];

const PROGRAMMER_OPERATORS: Operator[] = ['AND', 'OR', 'XOR', 'LSH', 'RSH', 'MOD'];
const HEX_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];
const BASES: Base[] = ['hex', 'dec', 'oct', 'bin'];
const BASE_LABELS: Record<Base, string> = { hex: 'HEX', dec: 'DEC', oct: 'OCT', bin: 'BIN' };

const KEY_OPERATORS: Record<string, Operator> = {
  '+': '+',
  '-': '−',
  '*': '×',
  '/': '÷',
  '%': 'MOD',
};

const MODE_LABELS: Record<Mode, string> = {
  standard: 'Standard',
  programmer: 'Programmer',
  convert: 'Convert',
};

export function Calculator({ api }: { api: HangarAPI }) {
  const [state, dispatch] = useReducer(reduce, undefined, initialState);
  // §34.3 — which category and which two units, kept beside the calculator's state rather than in
  // it: switching to standard and back must not forget that you were converting knots.
  const [units, setUnits] = useState(initialConvert);

  useEffect(() => {
    void api.window.setTitle(
      state.mode === 'standard' ? 'Calculator' : `Calculator — ${MODE_LABELS[state.mode]}`,
    );
  }, [api, state.mode]);

  const onKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    const key = event.key;
    if (/^[0-9]$/.test(key) || /^[a-fA-F]$/.test(key)) {
      dispatch({ type: 'digit', digit: key });
    } else if (key === '.') {
      dispatch({ type: 'point' });
    } else if (KEY_OPERATORS[key]) {
      dispatch({ type: 'operator', operator: KEY_OPERATORS[key] });
    } else if (key === 'Enter' || key === '=') {
      dispatch({ type: 'equals' });
    } else if (key === 'Backspace') {
      dispatch({ type: 'backspace' });
    } else if (key === 'Escape' || key.toLowerCase() === 'c') {
      dispatch({ type: 'clear' });
    } else {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const readout = useMemo(() => baseReadout(state), [state]);
  const digits = BASE_DIGITS[state.base];

  /**
   * §34.3 — the converted value, formatted the way every other result in this app is.
   *
   * `formatValue`'s 12-significant-digit trim is what turns an exact ratio into something a person
   * reads: 1 in is 25.4 mm and not `25.400000000000002`, and 1 GiB is 1.073741824 GB rather than a
   * number in scientific notation.
   */
  const converted = useMemo(() => {
    if (state.mode !== 'convert') return '';
    const result = convert(parseEntry(state.entry, 'dec'), units.category, units.from, units.to);
    return result === null ? 'Error' : formatValue(result, 'dec', 'standard');
  }, [state.mode, state.entry, units]);

  return (
    <div
      className="calc"
      data-testid="calculator"
      data-mode={state.mode}
      tabIndex={0}
      onKeyDown={onKeyDown}
      // Keyboard-driven (§11.9): the app takes focus on mount so typing just works.
      ref={(node) => node?.focus()}
    >
      <div className="calc-modes" role="tablist" aria-label="Calculator mode">
        {(['standard', 'programmer', 'convert'] as Mode[]).map((mode) => (
          <button
            key={mode}
            type="button"
            role="tab"
            aria-selected={state.mode === mode}
            className="calc-mode"
            data-testid={`calc-mode-${mode}`}
            data-active={String(state.mode === mode)}
            onClick={() => dispatch({ type: 'mode', mode })}
          >
            {MODE_LABELS[mode]}
          </button>
        ))}
      </div>

      <output className="calc-display" data-testid="calc-display">
        {state.entry}
      </output>

      {/* §34.3 — the converter. The **display is the input**: the pad above types into it exactly as
          it does in standard mode, so "type a number, tab between the dropdowns" needs no second
          field to keep in sync with the first one. */}
      {state.mode === 'convert' ? (
        <div className="calc-convert" data-testid="calc-convert">
          <div className="calc-categories" role="tablist" aria-label="Category">
            {CONVERT_CATEGORIES.map((category) => (
              <button
                key={category}
                type="button"
                role="tab"
                aria-selected={units.category === category}
                className="calc-category"
                data-testid={`calc-category-${category}`}
                data-active={String(units.category === category)}
                onClick={() => setUnits((current) => setConvertCategory(current, category))}
              >
                {CATEGORY_LABELS[category]}
              </button>
            ))}
          </div>

          <div className="calc-units">
            <label className="calc-unit">
              <span className="calc-unit-label">From</span>
              <select
                data-testid="calc-unit-from"
                value={units.from}
                onChange={(event) => setUnits((current) => ({ ...current, from: event.target.value }))}
              >
                {unitsOf(units.category).map((unit) => (
                  <option key={unit.label} value={unit.label}>
                    {unit.label}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="calc-key"
              data-kind="function"
              data-testid="calc-unit-swap"
              aria-label="Swap units"
              onClick={() => setUnits((current) => ({ ...current, from: current.to, to: current.from }))}
            >
              ⇄
            </button>
            <label className="calc-unit">
              <span className="calc-unit-label">To</span>
              <select
                data-testid="calc-unit-to"
                value={units.to}
                onChange={(event) => setUnits((current) => ({ ...current, to: event.target.value }))}
              >
                {unitsOf(units.category).map((unit) => (
                  <option key={unit.label} value={unit.label}>
                    {unit.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <output className="calc-converted" data-testid="calc-converted">
            {converted}
          </output>
        </div>
      ) : null}

      {state.mode === 'programmer' ? (
        <div className="calc-bases" data-testid="calc-bases">
          {BASES.map((base) => (
            <button
              key={base}
              type="button"
              className="calc-base"
              data-testid={`calc-base-${base}`}
              data-active={String(state.base === base)}
              onClick={() => dispatch({ type: 'base', base })}
            >
              <span className="calc-base-label">{BASE_LABELS[base]}</span>
              <span className="calc-base-value" data-testid={`calc-value-${base}`}>
                {readout[base]}
              </span>
            </button>
          ))}
        </div>
      ) : null}

      <div className="calc-pad">
        {state.mode === 'programmer'
          ? HEX_LETTERS.map((letter) => (
              <button
                key={letter}
                type="button"
                className="calc-key"
                data-testid={`calc-hex-${letter}`}
                disabled={!digits.includes(letter)}
                onClick={() => dispatch({ type: 'digit', digit: letter })}
              >
                {letter}
              </button>
            ))
          : null}

        {STANDARD_KEYS.map((key) => (
          <button
            key={key.label}
            type="button"
            className="calc-key"
            data-kind={key.kind ?? 'digit'}
            data-testid={`calc-key-${key.label}`}
            disabled={key.action.type === 'digit' && !digits.includes(key.action.digit)}
            onClick={() => dispatch(key.action)}
          >
            {key.label}
          </button>
        ))}

        {state.mode === 'programmer'
          ? PROGRAMMER_OPERATORS.map((operator) => (
              <button
                key={operator}
                type="button"
                className="calc-key"
                data-kind="operator"
                data-testid={`calc-key-${operator}`}
                onClick={() => dispatch({ type: 'operator', operator })}
              >
                {operator}
              </button>
            ))
          : null}
      </div>

      <ol className="calc-history" data-testid="calc-history">
        {state.history.map((entry, index) => (
          <li key={`${entry.expression}-${index}`} className="calc-history-entry">
            <span className="calc-history-expression">{entry.expression}</span>
            <span className="calc-history-result">{entry.result}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}
